"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/components/feedback";
import { useAccessToken, useTenantData } from "@/hooks";
import { QueryKeys } from "@/lib/types";
import {
  fetchStudioFeatureSettings,
  type StudioFeatureSettingsResult,
  upsertStudioFeatureSettings,
} from "@/lib/utils/feature-settings/feature-settings-api";
import {
  DEFAULT_STUDIO_FEATURE_SETTINGS,
  DEFAULT_STUDIO_FEATURE_SETTINGS_PERSISTED,
} from "@/lib/utils/feature-settings/feature-settings-defaults";

type UseStudioFeatureSettingsOptions = {
  enabled?: boolean;
};

export function useStudioFeatureSettings({
  enabled = true,
}: UseStudioFeatureSettingsOptions = {}) {
  const { tenantId } = useTenantData();
  const accessToken = useAccessToken();

  const query = useQuery({
    queryKey: [QueryKeys.StudioFeatureSettings, tenantId],
    queryFn: () => {
      if (!tenantId) {
        return {
          ...DEFAULT_STUDIO_FEATURE_SETTINGS,
          persisted: { ...DEFAULT_STUDIO_FEATURE_SETTINGS_PERSISTED },
        } satisfies StudioFeatureSettingsResult;
      }
      return fetchStudioFeatureSettings(tenantId, {
        accessToken: accessToken ?? undefined,
      });
    },
    enabled: enabled && !!tenantId,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });

  return {
    makeupsEnabled:
      query.data?.makeupsEnabled ??
      DEFAULT_STUDIO_FEATURE_SETTINGS.makeupsEnabled,
    appointmentsEnabled:
      query.data?.appointmentsEnabled ??
      DEFAULT_STUDIO_FEATURE_SETTINGS.appointmentsEnabled,
    timeClockEnabled:
      query.data?.timeClockEnabled ??
      DEFAULT_STUDIO_FEATURE_SETTINGS.timeClockEnabled,
    homepageEnabled:
      query.data?.homepageEnabled ??
      DEFAULT_STUDIO_FEATURE_SETTINGS.homepageEnabled,
    choreographerPortalEnabled:
      query.data?.choreographerPortalEnabled ??
      DEFAULT_STUDIO_FEATURE_SETTINGS.choreographerPortalEnabled,
    contractorsEnabled:
      query.data?.contractorsEnabled ??
      DEFAULT_STUDIO_FEATURE_SETTINGS.contractorsEnabled,
    ticketsEnabled:
      query.data?.ticketsEnabled ??
      DEFAULT_STUDIO_FEATURE_SETTINGS.ticketsEnabled,
    persisted: query.data?.persisted ?? {
      ...DEFAULT_STUDIO_FEATURE_SETTINGS_PERSISTED,
    },
    isPending: query.isPending,
    isFetching: query.isFetching,
    isError: query.isError,
    refetch: query.refetch,
  };
}

export function useStudioFeatureSettingsUpdate() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const accessToken = useAccessToken();
  const { tenantId } = useTenantData();

  return useMutation({
    mutationFn: async (
      request: StudioFeatureSettingsResult,
    ): Promise<StudioFeatureSettingsResult> => {
      if (!accessToken || !tenantId) {
        throw new Error("Authentication token is required");
      }
      return upsertStudioFeatureSettings(tenantId, request, accessToken);
    },
    onSuccess: async () => {
      if (tenantId) {
        await queryClient.invalidateQueries({
          queryKey: [QueryKeys.StudioFeatureSettings, tenantId],
        });
      }

      toast({
        status: "success",
        title: "Studio features updated",
        description: "Module visibility has been saved for your studio.",
      });
    },
    onError: (error: unknown) => {
      toast({
        status: "error",
        title: "Could not save studio features",
        description:
          error instanceof Error ? error.message : "Something went wrong.",
      });
    },
  });
}
